/*
 * Cart behaviour - GoKwik compatibility + "add to cart never navigates"
 * ---------------------------------------------------------------------
 *
 * PART 1 - the drawer (original purpose of this file)
 *   #CartDrawer is a <dialog> opened with showModal(), which promotes it into
 *   the browser's top layer. Top-layer elements paint above ALL z-indexed
 *   content, so GoKwik's popup (z-index 2147483647) can never rise above it -
 *   the drawer hides the popup and Checkout looks like it does nothing.
 *
 *   So: showModal()/show() are overridden to navigate to the real /cart page.
 *   With no modal dialog on screen GoKwik's popup layers normally.
 *
 *   The rule is inverted from how this started, which is what makes it work
 *   everywhere: navigate ONLY when the shopper explicitly asked for their cart
 *   (the open follows a click on a cart control). Any other drawer open - an
 *   add-to-cart confirmation, an upsell, anything programmatic - is swallowed.
 *   Inverting it means we never have to enumerate the add-to-cart paths, so
 *   cards, quick-add, quick-view, PDP and the sticky bar are all covered, and
 *   any future add path is covered for free.
 *
 * PART 2 - the native form post (2026-08)
 *   Part 1 alone did not stop cards redirecting to /cart, because this store's
 *   theme setting is cart_type = "notification", not "drawer" - so #CartDrawer
 *   is not what runs after an add here, and patching it changed nothing.
 *
 *   The remaining path is a plain form POST to /cart/add, which Shopify answers
 *   with a 302 to /cart. That is a full page navigation, nothing to do with the
 *   drawer, and it matches the symptom exactly: you land on the cart page and
 *   the count has gone up.
 *
 *   So this file now also catches product-form submits that reach document
 *   WITHOUT having been prevented, and performs the add over fetch instead.
 *
 *   The defaultPrevented check is what makes this safe: if the theme's own
 *   handler already took the form (the normal AJAX path, e.g. on the PDP) it
 *   will have called preventDefault long before the event bubbles this far, and
 *   this listener stands down. It only engages on a submit that was genuinely
 *   about to cause a page load. If the diagnosis above is wrong, this is a
 *   no-op rather than a second bug.
 *
 *   After a successful add the cart badge is repainted from /cart.js - the
 *   badge is the shopper's only confirmation now, so it must not rely on the
 *   page reload that used to refresh it.
 *
 * 2026-08 PERFORMANCE FIX
 *   This file used to keep a permanent MutationObserver on document.body to
 *   re-patch the drawer after section re-renders. That observer ran on EVERY
 *   DOM mutation site-wide, stacking on top of GoKwik's own body-wide observer.
 *   A short bounded retry covers late rendering instead.
 *
 * TO REVERT: delete the <script> tag for this file from layout/theme.liquid.
 */
(function () {
  // A cart control click and the drawer open it causes happen in the same tick.
  var EXPLICIT_WINDOW_MS = 1500;

  // Attribute-based on purpose - no theme class names to drift out of date.
  // Matched with closest(), so clicks on the icon's <svg> or count bubble count.
  var SEL_CART_CONTROL = '[aria-controls="CartDrawer"], a[href$="/cart"]';

  var lastCartClickAt = 0;

  function cartUrl() {
    var base = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        base = window.Shopify.routes.root;
      }
    } catch (e) {}
    if (base.charAt(base.length - 1) === '/') base = base.slice(0, -1);
    return base + '/cart';
  }

  // ---- Part 1: drawer -------------------------------------------------------

  // Capture phase: runs before the theme's own handler calls showModal().
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest(SEL_CART_CONTROL)) lastCartClickAt = Date.now();
    },
    true
  );

  function patch(el) {
    if (!el) return false;
    if (el.__tuCartPageMode) return true;
    el.__tuCartPageMode = true;

    var go = function () {
      // Not preceded by a cart control click - treat as an add confirmation.
      if (Date.now() - lastCartClickAt > EXPLICIT_WINDOW_MS) return;

      var url = cartUrl();
      if (window.location.pathname !== url) {
        window.location.href = url;
      }
    };

    try {
      el.showModal = go;
      el.show = go;
    } catch (e) {}

    return true;
  }

  // ---- Part 2: badge + native form post fallback ----------------------------

  function paintBadge(count) {
    var nodes = document.querySelectorAll('hdt-cart-count');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = String(count);
    }
    var fav = document.querySelector('favicon-cart-badge');
    if (fav) fav.setAttribute('cart-count', String(count));
  }

  // Distinct products, matching snippets/header_group_icons.liquid. Fabric is
  // stored in 0.25 m units, so a unit count would read 4 after a 1 metre add.
  function refreshBadge() {
    return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        paintBadge((cart.items || []).length);
      })
      .catch(function () {});
  }

  // Bubble phase, so every theme handler has had its chance first.
  document.addEventListener(
    'submit',
    function (e) {
      // Already handled by the theme's AJAX path - leave it alone.
      if (e.defaultPrevented) return;

      var form = e.target;
      if (!form || typeof form.getAttribute !== 'function') return;

      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      // This submit was about to navigate. Take it over.
      e.preventDefault();

      fetch(cartUrl() + '/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      }).then(refreshBadge, refreshBadge);
    },
    false
  );

  function init() {
    if (patch(document.getElementById('CartDrawer'))) return;

    // Bounded retry for late rendering - no permanent observer.
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      if (patch(document.getElementById('CartDrawer')) || tries > 20) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
